import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { Language, StoryVariant, getTranslations } from "./translations";
import { getStoryVariant } from "@/lib/posthog";
import { useCMS } from "@/lib/CMSContext";

type LanguageContextType = {
  language: Language;
  setLanguage: (lang: Language) => void;
  variant: StoryVariant;
  setVariant: (v: StoryVariant) => void;
  t: (key: string) => string;
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const SUPPORTED: Language[] = ["en", "ro", "sr", "mk", "bg", "hu"];

function detectBrowserLanguage(): Language {
  const saved = localStorage.getItem("vd-lang");
  if (saved && SUPPORTED.includes(saved as Language)) return saved as Language;

  const langs = navigator.languages ?? [navigator.language];
  for (const raw of langs) {
    const code = raw.split("-")[0].toLowerCase();
    if (SUPPORTED.includes(code as Language)) return code as Language;
  }
  return "en";
}

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLang] = useState<Language>(detectBrowserLanguage);
  const [variant, setVariantState] = useState<StoryVariant>("universal");
  const { cms } = useCMS();

  useEffect(() => {
    const timer = setTimeout(() => setVariantState(getStoryVariant()), 300);
    return () => clearTimeout(timer);
  }, []);

  const setLanguage = (lang: Language) => {
    setLang(lang);
    localStorage.setItem("vd-lang", lang);
    document.documentElement.lang = lang;
  };

  const setVariant = (v: StoryVariant) => {
    setVariantState(v);
    localStorage.setItem("vd-story-variant", v);
  };

  const t = (key: string): string => {
    const override = cms.overrides[language]?.[key];
    if (override) return override;
    const trans = getTranslations(language, variant);
    return (trans as Record<string, string>)[key] || key;
  };

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, variant, setVariant, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useLanguage must be used within LanguageProvider");
  return context;
};
